import { useState, useMemo } from 'react';
import {
  Input,
  Badge,
  Alert,
  Button,
  ButtonGroup,
  FormGroup,
  Label,
  Card,
  CardBody,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useBrand } from '../../brand/BrandProvider';
import {
  useNexusTenantRouting,
  useSetNexusTenantRouting,
  useClearNexusTenantRouting,
} from './useNexusTenantRouting';
import { useNexusPlugins } from '../NexusPlugins/useNexusPlugins';

/**
 * Nexus Tenant-Plugin-Routing (Spec 2 von 8).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-tenant-routing.md
 * Endpoints: /v1/admin/tenants/:tenantId/routing (GET/PUT/DELETE)
 *
 * Per Tenant entscheiden welches Plugin für welche Kind (domain/hosting/
 * game/vps/cloud) verwendet wird. Default kommt aus plugins.is_default,
 * Override aus tenant_plugin_routing.
 */
export function NexusTenantRoutingPage() {
  const brand = useBrand();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  // Initial: currentBrand wenn vorhanden, sonst erste Brand der Liste
  const [tenantId, setTenantId] = useState(() => {
    if (brand.currentBrand?.id) return String(brand.currentBrand.id);
    return brand.brands?.[0]?.id ? String(brand.brands[0].id) : '';
  });

  const routing = useNexusTenantRouting(tenantId);
  const plugins = useNexusPlugins();
  const setMutation = useSetNexusTenantRouting();
  const clearMutation = useClearNexusTenantRouting();

  const rows = routing.data?.rows ?? [];
  const allPlugins = plugins.data?.plugins ?? [];

  const pluginsByKind = useMemo(() => {
    const map = {};
    for (const p of allPlugins) {
      if (!map[p.kind]) map[p.kind] = [];
      if (p.enabled) map[p.kind].push(p);
    }
    return map;
  }, [allPlugins]);

  const onSetOverride = async (kind, pluginId) => {
    if (!pluginId) {
      await clearMutation.mutateAsync({ tenantId, kind });
    } else {
      await setMutation.mutateAsync({ tenantId, kind, plugin_id: pluginId });
    }
  };

  return (
    <div>
      <PageHeader
        title="Nexus · Tenant-Plugin-Routing"
        subtitle="Per Tenant: welches Plugin für welche Resource-Kind aktiv ist"
      />

      <Alert color="info" className="small">
        Default ist <code>plugins.is_default = 1</code> für jede Kind. Eine
        Override-Row in <code>tenant_plugin_routing</code> überschreibt das
        nur für diesen Tenant. Backend feuert nach jeder Mutation
        <code> nexus.plugins.invalidate</code> sodass der Plugin-Manager
        die Credentials-Cache-Einträge droppt.
      </Alert>

      <Card className="mb-3">
        <CardBody>
          <FormGroup className="mb-0">
            <Label>Tenant</Label>
            <Input
              type="select"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              style={{ maxWidth: 360 }}
            >
              {(brand.brands ?? []).map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.displayName ?? b.slug} (#{b.id} · {b.brandOrigin})
                </option>
              ))}
            </Input>
          </FormGroup>
        </CardBody>
      </Card>

      <DataTable
        loading={routing.isLoading || plugins.isLoading}
        error={routing.error}
        empty={!routing.isLoading && rows.length === 0}
        emptyMessage="Keine Routing-Daten — Backend liefert nichts oder Tenant existiert nicht."
        onRetry={() => routing.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 120 }}>Kind</th>
            <th style={{ width: 220 }}>Default</th>
            <th>Override</th>
            <th style={{ width: 160 }} className="text-end">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const available = pluginsByKind[r.kind] ?? [];
            const isOverridden = !!r.override_plugin_id;
            return (
              <tr key={r.kind}>
                <td>
                  <Badge color="secondary" pill className="orbit-code-badge-sm">{r.kind}</Badge>
                </td>
                <td>
                  {r.default_plugin_id ? (
                    <code>{r.default_plugin_id}</code>
                  ) : (
                    <span className="text-muted small">kein Default</span>
                  )}
                </td>
                <td>
                  {canWrite ? (
                    <Input
                      type="select"
                      bsSize="sm"
                      value={r.override_plugin_id ?? ''}
                      onChange={(e) => onSetOverride(r.kind, e.target.value || null)}
                      disabled={setMutation.isPending || clearMutation.isPending}
                      style={{ maxWidth: 280 }}
                    >
                      <option value="">— Default verwenden —</option>
                      {available.map((p) => (
                        <option key={p.identifier} value={p.identifier}>
                          {p.identifier}
                        </option>
                      ))}
                    </Input>
                  ) : r.override_plugin_id ? (
                    <code>{r.override_plugin_id}</code>
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </td>
                <td className="text-end">
                  {isOverridden && canWrite && (
                    <Button
                      color="secondary"
                      size="sm"
                      outline
                      onClick={() => onSetOverride(r.kind, null)}
                      disabled={clearMutation.isPending}
                    >
                      ↺ Reset
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </DataTable>

      {!canWrite && (
        <Alert color="secondary" className="small mt-3 mb-0">
          Nur-Lese-Modus — Permission <code>integrations.write</code> fehlt.
        </Alert>
      )}
    </div>
  );
}
